import { SubSink } from "subsink";
import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { BrandInfoEntry } from "@apps/patient-core/src/app/data_model/brand-info";
import { BaseCommonService } from "@apps/patient-core/src/app/services/abstracts/common.service";
import { BottomSheetService } from "@apps/patient-core/src/app/services/bottom-sheet.service";
import { RouterModule, RouterOutlet } from "@angular/router";
import { SHARED } from "@apps/patient-core/src/app/shared";
import { FADE_IN_ANIMATION, ROUTE_ANIMATIONS } from "@apps/patient-core/src/app/utils/animations";
import { NgIconComponent, provideIcons } from "@ng-icons/core";
import { heroArrowLeftOnRectangle } from "@ng-icons/heroicons/outline";
import { environment } from "@apps/patient-core/src/app/environments/environment";
import { hideAppLoadingSpinner } from "@apps/patient-core/src/app/utils";
import { OverlayService } from "@apps/patient-core/src/app/services/overlay.service";
import { ConciergeLoginService } from "../../services/concierge-login.service";
import { ConciergeService } from "../../services/concierge.service";
import { DiagnosticsComponent } from "../diagnostics/diagnostics.component";
import { BaseAuthService } from "@apps/patient-core/src/app/services/abstracts/auth.service";
import { BasePatientsService } from "@apps/patient-core/src/app/services/abstracts/patients.service";
import { ActivityMonitorService } from "@apps/patient-core/src/app/services/activity-monitor.service";

@Component({
  selector: "dentr-main",
  templateUrl: "./main.component.html",
  standalone: true,
  imports: [RouterModule, SHARED, NgIconComponent],
  animations: [ROUTE_ANIMATIONS, FADE_IN_ANIMATION],
  host: { class: "block h-full" },
  providers: [provideIcons({ heroArrowLeftOnRectangle })],
})
export class ConciergeMainComponent implements OnInit, OnDestroy, AfterViewChecked {
  private _subs = new SubSink();
  private _appView: boolean;
  private _loading = true;
  public brand: BrandInfoEntry;
  public cachedBrand: BrandInfoEntry;
  public showMenu = false;
  public appVersion = environment.VERSION;
  get appView(): boolean {
    return this._appView;
  }
  @Input() set appView(value: boolean) {
    this._appView = value;
    this.hideUserMenu = this._authService.isPatientUnauthenticated() || this._appView;
  }
  @Input()
  public hideTopNav = false;

  /**
   * ** IMPORTANT **
   *
   * Do not remove this function because it prevents PI from being exposed in Safari after the patient has logged out by clicking the back button
   *
   * @param event
   */
  @HostListener("window:pageshow", ["$event"])
  public onWindowShow(event) {
    if (event.persisted) {
      // Clear the body to ensure that the page does not expose any PI
      const body = document.querySelector("body");
      if (body) body.innerHTML = "";

      // Reload the page to ensure that we get go back to the login screen
      window.location.reload();
    }
  }

  @HostListener("document:click")
  @HostListener("document:keypress")
  @HostListener("document:touchstart")
  @HostListener("document:touchend")
  @HostListener("document:touchcancel")
  @HostListener("document:touchmove")
  public onPatientActivity() {
    this._activityMonitorService.track();
  }

  @HostListener("window:blur")
  public onBlur() {
    this._activityMonitorService.onBlur();
  }

  @HostListener("window:focus")
  public onFocus() {
    this._activityMonitorService.onFocus();
  }

  @ViewChild("mainContentWrapper") mainContentWrapper: ElementRef;
  public hideUserMenu = true;

  constructor(
    private _commonService: BaseCommonService,
    private _authService: BaseAuthService,
    private _bottomSheetService: BottomSheetService,
    private _cdr: ChangeDetectorRef,
    private _conciergeLoginService: ConciergeLoginService,
    private _conciergeService: ConciergeService,
    private _activityMonitorService: ActivityMonitorService,
    private _overlayService: OverlayService,
    public patientsService: BasePatientsService
  ) {}

  public get loading(): boolean {
    return this._loading;
  }

  public set loading(value: boolean) {
    const content_wrapper = document.getElementById("content-wrapper");
    if (content_wrapper) content_wrapper.remove();
    hideAppLoadingSpinner();

    this._loading = value;
  }

  // #region Angular lifecycle hooks
  public prepareRoute(outlet: RouterOutlet) {
    // Disable animations for e2e tests
    const isE2E = window.localStorage.getItem("e2e");
    if (isE2E) return;
    return outlet?.isActivated && (outlet.activatedRouteData?.animation ? outlet.activatedRouteData.animation : outlet.activatedRoute);
  }

  // Needed to ensure the animation change detection is triggered correctly
  public ngAfterViewChecked() {
    this._cdr.detectChanges();
  }

  public ngOnInit() {
    this._bindEvents();

    if (this._authService.isPatientInPractice()) {
      this._conciergeLoginService.startInactivityMonitor();
    }

    this.loading = false;
  }

  public signout(): void {
    this._conciergeLoginService.navigateToLogout();
  }

  public get canLogout(): boolean {
    return this._authService.isPatientInPractice() && !this.appView;
  }

  public get isPatientInPractice(): boolean {
    return this._authService.isPatientInPractice();
  }

  public ngOnDestroy() {
    this._subs.unsubscribe();
  }
  // #endregion

  // #region event subscriptions
  private _bindEvents() {
    this._subs.sink = this._commonService.onInitData.subscribe(
      () => {
        this.loading = false;
      },
      () => {
        // Bypass the loading setter so that the content wrapper is not removed
        this._loading = false;
      }
    );

    this._subs.sink = this._bottomSheetService.onBottomSheetOffset.subscribe((height) => {
      this.mainContentWrapper.nativeElement.style["padding-bottom"] = `${height}px`;
    });
  }
  // #endregion

  // #region Misc methods
  public get showConciergeDiagnostics() {
    return !!this._conciergeService.diagnosticsData;
  }

  public openConciergeDiagnostics() {
    if (!this.showConciergeDiagnostics) return;

    this._overlayService.open({
      component: DiagnosticsComponent,
    });
  }
  // #endregion
}
